import { useDecisions } from '@libs/common-next';

import { useProduct } from '@/entities/product';
import { type FormFields, getCustomerFrequencySelection } from '@/entities/purchase-form';
import { resolveAutoshipFrequencySelection } from '@/features/select-frequency/mapRecommendedAutoshipFrequency';

import { buildLines, resolveDeliveryMethod } from '../lib';

export const useGetDefaultValues = () => {
    const { product } = useProduct();
    const isDynamicFrequencyEnabled = useDecisions('dyn_as_freq');
    const as_freq_5_7 = useDecisions('as_freq_5_7');

    const defaultPurchaseOption: FormFields['purchaseOption'] = 'one-time';
    // Personalization
    // NOTE: Only the first line of text can be configured as mandatory - whether front or back line(s) of text.
    const isCustomizationMandatory = Boolean(product?.personalization?.isCustomizationMandatory);
    const colorStyle = product?.personalization?.styleOptions?.threadColors?.[0];
    const fontFace = product?.personalization?.fontFaceOptions?.[0];

    const frontLines = buildLines({
        firstIsRequired: isCustomizationMandatory,
        numbers: product?.personalization?.textLocations?.frontLines ?? [],
    });

    const backLines = buildLines({
        firstIsRequired: isCustomizationMandatory,
        numbers: product?.personalization?.textLocations?.backLines ?? [],
    });

    // Fulfilment (delivery) method
    const deliveryMethods = product?.deliveryMethods;
    const deliveryMethod = resolveDeliveryMethod({
        deliveryMethods,
        purchaseOption: defaultPurchaseOption,
    });

    // Default qty
    const defaultDeliveryMethod = deliveryMethod.toUpperCase() as 'BOPIS' | 'SDD' | 'STH';
    const quantity = product?.deliveryMethods[defaultDeliveryMethod].purchaseQuantities?.min ?? 1;

    const { autoshipFrequency, autoshipUnit } = resolveAutoshipFrequencySelection({
        isEnabled: isDynamicFrequencyEnabled,
        customerSelection: getCustomerFrequencySelection(product?.sku),
        recommendedFrequency: product?.recommendedAutoshipFrequency,
        recommendedUnit: product?.recommendedAutoshipFrequencyUnitOfMeasure,
        as_freq_5_7,
    });

    return {
        quantity,
        purchaseOption: defaultPurchaseOption,
        subscriptionType: 'new',
        autoshipUnit,
        autoshipFrequency,
        fulfilmentMethod: deliveryMethod,
        personalization: {
            ...(colorStyle && { colorStyle }),
            ...(fontFace && { fontFace }),
            frontLines,
            backLines,
        },
    } satisfies FormFields;
};
